/*
 * NOTE: Portions of this code have been adapted from the Jodit Editor (https://xdsoft.net/jodit/)
 * Released under MIT – see LICENSE.txt (https://github.com/xdan/jodit/blob/main/LICENSE.txt)
 * Copyright (c) 2013–2025 Valeriy Chupurnov. All rights reserved. https://xdsoft.net
 *
 * This implementation customizes Jodit’s link plugin methods to:
 * - Work with React and ReactDOM
 * - Integrate with a custom React-based LinkForm component
 *
 * Source File: https://github.com/xdan/jodit/blob/main/src/plugins/link/link.ts
 */
import type { IJoditEditorProps } from '@shared/core/jodit-react-ssr';
import { Jodit } from '@shared/core/jodit-react-ssr';
import type { SnapshotType } from 'jodit/types';
import { CustomLinkPluginOptions } from './CustomLink';

export const onSubmit = (jodit: Jodit, url: string, content: string, snapshot: SnapshotType, customAttributes: Record<string, string>): false => {
  // -------------------------
  // custom joy override start
  // -------------------------
  // Removed reading url and content from form instead passing in parameters
  // -------------------------
  //  -- end
  // -------------------------

  let links: HTMLAnchorElement[];

  // -------------------------
  // custom joy override start
  // -------------------------
  // These variables were present in the parent funcion __generateForm which is not used in this implementation
  // Instead we are using the parameters passed in to this function
  const isImageContent = Jodit.modules.Dom.isImage(jodit.s.current());
  const link = getLink(jodit);
  // -------------------------
  //  -- end
  // -------------------------

  jodit.s.restore();
  jodit.s.removeMarkers();
  jodit.editor.normalize();
  jodit.history.snapshot.restore(snapshot);
  const textWasChanged = getSelectionText(jodit, link) !== content.trim();

  const ci = jodit.createInside;
  if (!link || !Jodit.modules.Dom.isOrContains(jodit.editor, link)) {
    if (!jodit.s.isCollapsed()) {
      const node = jodit.s.current();

      if (Jodit.modules.Dom.isTag(node, 'img')) {
        links = [Jodit.modules.Dom.wrap(node, 'a', ci) as HTMLAnchorElement];
      } else {
        links = jodit.s.wrapInTag('a') as HTMLAnchorElement[];
      }
    } else {
      const a = ci.element('a');
      jodit.s.insertNode(a, false, false);
      links = [a];
    }

    links.forEach(link => jodit.s.select(link));
  } else {
    links = [link];
  }

  links.forEach(a => {
    Jodit.modules.Helpers.attr(a, 'href', url);

    // -------------------------
    // custom joy override start
    // -------------------------
    // Added custom attributes to the link
    Object.entries(customAttributes).forEach(([key, value]) => {
      Jodit.modules.Helpers.attr(a, key, value);
    });
    // -------------------------
    //  -- end
    // -------------------------

    if (!isImageContent) {
      writeImage(a, content, textWasChanged, url);
    }
  });

  jodit.synchronizeValues();

  // -------------------------
  // custom joy override start
  // -------------------------
  // Removed target_checkbox and no_follow_checkbox and  logic since we are using a custom react component for the link form
  // -------------------------
  //  -- end
  // -------------------------

  return false;
};
export const writeImage = (a: HTMLAnchorElement, content: string, textWasChanged: boolean, url: string): void => {
  let newContent = a.textContent;

  // -------------------------
  // custom joy override start
  // -------------------------
  // Use the content passed in to this function instead of the content input from the link form
  if (content.trim().length) {
    // -------------------------
    //  -- end
    // -------------------------

    if (textWasChanged) {
      newContent = content;
    }
  } else {
    // -------------------------
    // custom joy override start
    // -------------------------
    // Use the url passed in to this function instead of the url input from the link form
    newContent = url;
    // -------------------------
    //  -- end
    // -------------------------
  }

  const currentContent = a.textContent;

  if (newContent !== currentContent) {
    a.textContent = newContent;
  }
};

// -------------------------
// custom joy override start
// -------------------------
// This was a utility function inside __generateForm.
// We are using the same logic here with two parameters passed jodit and link
const getSelectionText = (jodit: Jodit, link: HTMLAnchorElement | false) => {
  // -------------------------
  //  -- end
  // -------------------------

  return link ? link.innerText : Jodit.modules.Helpers.stripTags(jodit.s.range.cloneContents(), jodit.ed);
};

// -------------------------
// custom joy override start
// -------------------------
// This is a utility function createed from utility function inside __generateForm.
// We are using the same logic here with two parameters passed jodit and link
export const getContentAndUrl = (jodit: Jodit) => {
  const current = jodit.s.current();
  const isImageContent = Jodit.modules.Dom.isImage(current);
  const link = getLink(jodit);
  const content = !isImageContent && current ? getSelectionText(jodit, link) : '';
  const url = link ? link.getAttribute('href') : '';

  return {
    content,
    url: url || '',
  };
};
// -------------------------
//  -- end
// -------------------------

/**
 * This is a utility function derived from code snippet inside __generateForm.
 */
export const getLink = (jodit: Jodit) => {
  let link: false | HTMLAnchorElement;
  // -------------------------
  // custom joy override start
  // -------------------------
  // Added focus to the editor before getting the link
  jodit.editor.focus();
  // -------------------------
  //  -- end
  // -------------------------
  const current = jodit.s.current();
  if (current && Jodit.modules.Dom.closest(current, 'a', jodit.editor)) {
    link = Jodit.modules.Dom.closest(current, 'a', jodit.editor) as HTMLAnchorElement;
  } else {
    link = false;
  }
  return link;
};

// -------------------------
// custom joy override start
// -------------------------
// This is a utility function derived from handler for unlink button click inside __generateForm.
// We are using the same logic here with two parameters passed jodit and snapshot
export const onUnlink = (jodit: Jodit, snapshot: SnapshotType): void => {
  // -------------------------
  //  -- end
  // -------------------------

  jodit.s.restore();
  jodit.history.snapshot.restore(snapshot);
  const link = getLink(jodit);

  if (link) {
    Jodit.modules.Dom.unwrap(link);
  }

  jodit.synchronizeValues();

  // -------------------------
  // custom joy override start
  // -------------------------
  // Removed event.PreventDefault() and close() since these will be handled in the custom react component for the link form
  // -------------------------
  //  -- end
  // -------------------------
};

// -------------------------
// custom joy override start
// -------------------------
// This was a utility function inside __generateForm.
// We are using the same logic here with two parameters passed jodit and html
export function onProcessPasteLink(jodit: Jodit, html: string): HTMLAnchorElement | void | true {
  // -------------------------
  //  -- end
  // -------------------------
  const { isURL, convertMediaUrlToVideoEmbed } = Jodit.modules.Helpers;
  const options = (jodit.o as unknown as IJoditEditorProps['config'] & { customLinkPlugin: CustomLinkPluginOptions }).customLinkPlugin;

  if (!isURL(html) || options.processPastedLink) {
    return;
  }

  jodit.e.stopPropagation('processPaste');
  if (options.processVideoLink) {
    const embed = Jodit.modules.Helpers.call(
      (options.video?.parseUrlToVideoEmbed ?? convertMediaUrlToVideoEmbed) as (url: string, options: { width?: number; height?: number }) => string,
      html,
      {
        width: options.video?.defaultWidth,
        height: options.video?.defaultHeight,
      },
    ) as string;

    if (embed !== html) {
      return jodit.createInside.fromHTML(embed) as HTMLAnchorElement;
    }
  }

  if (jodit.s.isCollapsed()) {
    const a = jodit.createInside.element('a');

    Jodit.modules.Helpers.attr(a, 'href', html);
    a.textContent = html;

    jodit.e.fire('applyLink', jodit, a, null);
    return a;
  }

  jodit.s.commitStyle({
    element: 'a',
    attributes: {
      href: html,
    },
  });

  return true;
}
